<template>
  <div>
    <v-select
      v-model="model"
      :options="formattedOptions"
      :filterable="false"
      @search="onSearch"
      :multiple="(multiple || multiple === 1)"
      :clereable="(multiple || multiple === 1)"
      ref="selectable"
      @input="onInput"
      label="label"
      @option:selected="emt => $emit('option:selected', emt)"
      :placeholder="$t('grid.widgets.units.search.placeholderSearchUnits')"
      :disabled="isTenantFilterEmpty"
      :class="{'facilities-select--scrollable': showSelectAll}"
    >
      <template #list-footer>
        <li class="pagination">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasPrevPage"
            @click="prevPage"
          >
            <feather-icon icon="ChevronLeftIcon" size="18"/>
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasNextPage"
            @click="nextPage"
          >
            <feather-icon icon="ChevronRightIcon" size="18"/>
          </b-button>
        </li>
      </template>
    </v-select>
    <div v-if="showSelectAll" class="mt-50 d-flex justify-content-end">
      <b-button
        variant="outline-secondary"
        size="sm"
        class="mr-50"
        :disabled="selectingAll || !hasSelectedFacilities"
        @click="clearAllFacilities"
      >
        {{ $t("schedule_alarm.clear_all_units") }}
      </b-button>
      <b-button
        variant="outline-primary"
        size="sm"
        :disabled="isTenantFilterEmpty || selectingAll"
        :aria-busy="selectingAll ? 'true' : 'false'"
        @click="selectAllFacilities"
      >
        <b-spinner
          v-if="selectingAll"
          small
          class="mr-50"
          aria-hidden="true"
        />
        {{ $t(selectingAll
          ? "schedule_alarm.loading_all_units"
          : "schedule_alarm.select_all_units") }}
      </b-button>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import {debounce} from "lodash";
import {BButton, BSpinner} from "bootstrap-vue";
import axios from "@axios";

export default {
  name: "FacilitiesSelectPaginated",
  components: {
    vSelect,
    BButton,
    BSpinner,
  },
  props: {
    value: Array | Object,
    multiple: {
      type:  Boolean | Number,
      default: false
    },
    tenantIds: {
      type: Array,
      default: null,
    },
    categoryIds: {
      type: Array,
      default: null,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    isSaturday: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      model: null,
      search: "",
      offset: 0,
      limit: 8,
      currentPage: 1,
      meta: null,
      options: [],
      selectingAll: false,
    };
  },
  created() {
    this.model = this.value || null;
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    value(newValue) {
      this.model = newValue || null;
    },
    tenantIds: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.filterSelectedFacilities();
        this.fetchData();
      },
    },
    categoryIds: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.filterSelectedFacilities();
        this.fetchData();
      },
    },
    isSaturday: {
      handler() {
        this.currentPage = 1;
        this.filterSelectedFacilities();
        this.fetchData();
      },
    },
    currentPage() {
      this.fetchData();
    },
  },
  computed: {
    formattedOptions() {
      return this.options
        .map((item) => {
          return {
            ...item,
            value: item.id,
            label: item.description,
          }
        });
    },
    isTenantFilterEmpty() {
      return Array.isArray(this.tenantIds) && this.tenantIds.length === 0;
    },
    hasSelectedFacilities() {
      return Array.isArray(this.model) && this.model.length > 0;
    },
    hasNextPage() {
      if (this.meta) {
        return this.meta.current_page <= this.meta.last_page
      }
      return false;
    },
    hasPrevPage() {
      if (this.meta) {
        return this.meta.current_page - 1 > 0;
      }
      return false;
    },
  },
  methods: {
    onInput() {
      this.$emit('input', this.model || null);
    },
    clearAllFacilities() {
      this.model = [];
      this.$emit('input', this.model);
    },
    async selectAllFacilities() {
      if (this.isTenantFilterEmpty || this.selectingAll) return;

      this.selectingAll = true;
      const params = {
        max: 100,
        "page[n]": 1,
        sortBy: "description",
        sortDesc: false,
      };

      if (Array.isArray(this.tenantIds)) {
        params["tenant[]"] = this.tenantIds;
      }
      if (Array.isArray(this.categoryIds) && this.categoryIds.length) {
        params.category = this.categoryIds;
      }

      if (this.isSaturday) {
        params.is_saturday = this.isSaturday;
      }


      try {
        const {data: firstResponse} = await axios.get('/v1/unit', {params});
        let facilities = firstResponse.data || [];
        const lastPage = firstResponse.meta ? firstResponse.meta.last_page : 1;

        if (lastPage > 1) {
          const requests = [];
          for (let page = 2; page <= lastPage; page++) {
            requests.push(axios.get('/v1/unit', {
              params: {...params, "page[n]": page},
            }));
          }
          const responses = await Promise.all(requests);
          responses.forEach(response => {
            facilities = facilities.concat(response.data.data || []);
          });
        }

        this.model = facilities.map(item => ({
          ...item,
          value: item.id,
          label: item.description,
        }));
        this.$emit('input', this.model);
      } catch (error) {
        console.log("FacilitiesSelectPaginated: select all request error", error.response);
      } finally {
        this.selectingAll = false;
      }
    },
    filterSelectedFacilities() {
      if (!Array.isArray(this.model)) return;

      this.model = this.model.filter(unit => {
        const tenantId = unit.router ? unit.router.id_tenant : unit.id_tenant;
        const categoryId = unit.category?.id ?? unit.id_category;
        const matchesTenant = !Array.isArray(this.tenantIds) ||
          !tenantId || this.tenantIds.includes(tenantId);
        const matchesCategory = !Array.isArray(this.categoryIds) ||
          this.categoryIds.length === 0 ||
          (categoryId != null && this.categoryIds.includes(categoryId));
        return matchesTenant && matchesCategory;
      });
      this.$emit('input', this.model);
    },
    fetchData() {
      if (this.isTenantFilterEmpty) {
        this.options = [];
        this.meta = null;
        return Promise.resolve();
      }

      const params = {
        q: this.search,
        max: this.limit,
        "page[n]": this.currentPage,
        sortBy: "description",
        sortDesc: false,
      };

      if (Array.isArray(this.tenantIds)) {
        params["tenant[]"] = this.tenantIds;
      }
      if (Array.isArray(this.categoryIds) && this.categoryIds.length) {
        params.category = this.categoryIds;
      }

      if (this.isSaturday) {
        params.is_saturday = this.isSaturday;
      }

      return new Promise((resolve, reject) => {

        axios.get('/v1/unit', {
          params,
        })
          .then(({data}) => {
            this.meta = data.meta;
            this.options = data.data;
            resolve();
          })
          .catch((error) => {
            reject();
            console.log("FacilitiesSelectPaginated: request error", error.response);
          });
      });
    },
    onSearch: debounce(async function (query, loading) {
      if (query.length) {
        loading(true);
        this.search = query;
        this.currentPage = 1;
        await this.fetchData();
        loading(false);
      } else {
        this.currentPage = 1;
        this.search = "";
        await this.fetchData();
      }
    }, 500),
    nextPage() {
      this.currentPage += 1;
    },
    prevPage() {
      this.currentPage--;
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}

.pagination button {
  flex-grow: 1;

  &:first-child {
    margin-right: 5px;
  }
}

.pagination button:hover {
  cursor: pointer;
}

::v-deep .facilities-select--scrollable .vs__selected-options {
  max-height: 140px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-wrap: wrap;
  align-content: flex-start;
}
</style>
